import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ArrowUp, ArrowDown, Trash2, Image as ImageIcon, X, Pencil, Bot } from 'lucide-react';
import { discussionService, DiscussionComment } from '@services/discussionService';
import { useAuthStore } from '@store/authStore';
import { useDiscussionStore } from '@store/discussionStore';
import toast from 'react-hot-toast';
import ConfirmationModal from '@components/common/ConfirmationModal';
import AdminInstructionModal from '@components/common/AdminInstructionModal';
import MarkdownContent from '@components/common/MarkdownContent';
import { formatDate as formatDisplayDate, formatRelativeTime } from '@utils/dateUtils';
import DiscussionImage from './DiscussionImage';

// Content length limits (must match backend)
const MIN_COMMENT_LENGTH = 10;
const MAX_CONTENT_LENGTH = 5000;

interface CommentSectionProps {
    discussionId: string;
    comments: DiscussionComment[];
    discussionOwner?: {
        user_id: string;
        user_name: string;
        user_profile_picture?: string;
    };
    botTriggerCount?: number;
}

interface MentionUser {
    user_id: string;
    user_name: string;
    user_profile_picture?: string;
}

interface CommentInputProps {
    user: any;
    newComment: string;
    setNewComment: (value: string) => void;
    commentImage: File | null;
    imagePreview: string | null;
    submitting: boolean;
    handleSubmitComment: (e: React.FormEvent) => void;
    handleImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleRemoveImage: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    participants: MentionUser[];
}

const CommentInput = ({
    user,
    newComment,
    setNewComment,
    commentImage,
    imagePreview,
    submitting,
    handleSubmitComment,
    handleImageSelect,
    handleRemoveImage,
    fileInputRef,
    participants
}: CommentInputProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Filter participants based on search
    const filteredParticipants = participants.filter(p =>
        p.user_name.toLowerCase().includes(mentionFilter.toLowerCase())
    );

    // Handle text change and detect @mentions
    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart || 0;

        // Find the last @ before cursor
        const textBeforeCursor = value.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            // Check if there's a space between @ and cursor (mention complete)
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                setShowMentionDropdown(true);
                setMentionFilter(textAfterAt);
                setMentionStartIndex(lastAtIndex);
            } else {
                setShowMentionDropdown(false);
            }
        } else {
            setShowMentionDropdown(false);
        }

        setNewComment(value);
    };

    // Insert mention into text
    const insertMention = (userName: string) => {
        if (mentionStartIndex === -1) return;

        const beforeMention = newComment.substring(0, mentionStartIndex);
        const afterMention = newComment.substring(mentionStartIndex + 1 + mentionFilter.length);
        const newText = `${beforeMention}@${userName} ${afterMention}`;

        setNewComment(newText);
        setShowMentionDropdown(false);
        setMentionFilter('');
        setMentionStartIndex(-1);

        // Focus back on textarea
        setTimeout(() => {
            textareaRef.current?.focus();
        }, 0);
    };

    return (
        <div className="bg-white/[0.08] border border-white/20 rounded-3xl p-4 md:p-6 mb-6 hover:border-purple-500/30 transition-all">
            <div className={`flex gap-3 md:gap-4 ${isExpanded ? 'items-start' : 'items-center'}`}>
                {/* User Avatar */}
                <div className={isExpanded ? 'pt-3' : ''}>
                    {((user?.profile_picture && user.profile_picture.trim() !== '') || (user?.picture && user.picture.trim() !== '')) ? (
                        <img
                            src={(user?.profile_picture && user.profile_picture.trim() !== '') ? user.profile_picture : user?.picture || ''}
                            alt={user?.username || 'User'}
                            className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-white/20 flex-shrink-0 object-cover"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold flex-shrink-0"
                        style={{ display: ((user?.profile_picture && user.profile_picture.trim() !== '') || (user?.picture && user.picture.trim() !== '')) ? 'none' : 'flex' }}
                    >
                        {(user?.full_name?.[0] || user?.username?.[0])?.toUpperCase() || 'U'}
                    </div>
                </div>

                {/* Input Area */}
                <div className="flex-1">
                    {!isExpanded ? (
                        // Collapsed state
                        <div
                            className="cursor-pointer"
                            onClick={() => setIsExpanded(true)}
                        >
                            <div className="text-purple-300/50 text-base md:text-lg font-sans py-3 hover:text-purple-300/70 transition-colors">
                                Join the discussion...
                            </div>
                        </div>
                    ) : (
                        // Expanded state
                        <form onSubmit={handleSubmitComment} className="space-y-4">
                            {/* Content */}
                            <div className="relative">
                                <textarea
                                    ref={textareaRef}
                                    value={newComment}
                                    onChange={handleTextChange}
                                    placeholder="Join the discussion... (type @ to mention)"
                                    rows={3}
                                    autoFocus
                                    maxLength={MAX_CONTENT_LENGTH}
                                    className="w-full bg-transparent border-none text-white text-base placeholder:text-white/30 focus:outline-none font-sans resize-none"
                                    disabled={submitting}
                                />
                                {/* Character Counter - Twitter style */}
                                <div className="flex items-center justify-end gap-2 mt-1">
                                    {newComment.length < MIN_COMMENT_LENGTH && newComment.length > 0 && (
                                        <span className="text-xs text-yellow-400/80 font-sans">
                                            Min {MIN_COMMENT_LENGTH} chars
                                        </span>
                                    )}
                                    <span className={`text-xs font-sans ${
                                        newComment.length > 0 && newComment.length < MIN_COMMENT_LENGTH
                                            ? 'text-yellow-400'
                                            : newComment.length > MAX_CONTENT_LENGTH - 100
                                                ? 'text-red-400'
                                                : newComment.length > MAX_CONTENT_LENGTH - 500
                                                    ? 'text-yellow-400'
                                                    : 'text-purple-300/60'
                                    }`}>
                                        {newComment.length}/{MAX_CONTENT_LENGTH}
                                    </span>
                                </div>

                                {/* Mention Dropdown */}
                                {showMentionDropdown && filteredParticipants.length > 0 && (
                                    <div className="absolute left-0 top-full mt-1 w-56 max-w-[80vw] bg-cosmic-900/95 backdrop-blur-xl border border-white/20 rounded-lg shadow-2xl z-50 max-h-40 overflow-y-auto">
                                        {filteredParticipants.map((participant) => (
                                            <button
                                                key={participant.user_id}
                                                type="button"
                                                onClick={() => insertMention(participant.user_name)}
                                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-purple-500/20 transition-colors text-left"
                                            >
                                                {participant.user_profile_picture ? (
                                                    <img
                                                        src={participant.user_profile_picture}
                                                        alt={participant.user_name}
                                                        className="w-6 h-6 rounded-full border border-white/20 object-cover flex-shrink-0"
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = 'none';
                                                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                                            if (fallback) fallback.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div
                                                    className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0"
                                                    style={{ display: participant.user_profile_picture ? 'none' : 'flex' }}
                                                >
                                                    {participant.user_name?.[0]?.toUpperCase() || 'U'}
                                                </div>
                                                <span className="text-white font-medium text-xs truncate">
                                                    @{participant.user_name}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Image Preview */}
                            {imagePreview && (
                                <div className="relative w-fit mx-auto">
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="max-h-64 md:max-h-80 w-auto rounded-xl border border-white/20 block"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg z-10"
                                    >
                                        <X size={14} className="text-white" />
                                    </button>
                                </div>
                            )}

                            {/* Divider */}
                            <div className="border-t border-white/10" />

                            {/* Actions */}
                            <div className="flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="p-2 rounded-lg bg-white/5 hover:bg-purple-500/20 text-purple-400 transition-all"
                                    title="Add image"
                                    disabled={submitting}
                                >
                                    <ImageIcon size={20} />
                                </button>

                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsExpanded(false);
                                            setNewComment('');
                                            handleRemoveImage();
                                        }}
                                        disabled={submitting}
                                        className="px-4 py-2 text-white/60 hover:text-white font-bold rounded-full transition-all text-sm md:text-base font-sans disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting || !newComment.trim() || newComment.trim().length < MIN_COMMENT_LENGTH}
                                        className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold py-2 px-6 rounded-full transition-all shadow-lg text-sm md:text-base font-sans disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                                    >
                                        {submitting ? 'Posting...' : 'Post'}
                                    </button>
                                </div>
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/jpg,image/png"
                                onChange={handleImageSelect}
                                className="hidden"
                            />
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

const CommentSection = ({ discussionId, comments, discussionOwner, botTriggerCount = 0 }: CommentSectionProps) => {
    const { user, isAuthenticated } = useAuthStore();
    const {
        addComment: addCommentToStore,
        updateCommentVote,
        updateCommentContent,
        removeComment,
        refreshComments,
    } = useDiscussionStore();

    // Build participants list from discussion owner + commenters (unique by user_id)
    const participants = useMemo(() => {
        const participantMap = new Map<string, MentionUser>();

        // Add discussion owner first
        if (discussionOwner) {
            participantMap.set(discussionOwner.user_id, {
                user_id: discussionOwner.user_id,
                user_name: discussionOwner.user_name,
                user_profile_picture: discussionOwner.user_profile_picture
            });
        }

        // Add commenters (includes BhriguGPT bot when it has commented)
        comments.forEach(comment => {
            if (!participantMap.has(comment.user_id)) {
                participantMap.set(comment.user_id, {
                    user_id: comment.user_id,
                    user_name: comment.user_name,
                    user_profile_picture: comment.user_profile_picture
                });
            }
        });

        return Array.from(participantMap.values());
    }, [discussionOwner, comments]);
    const [newComment, setNewComment] = useState('');
    const [commentImage, setCommentImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollCountRef = useRef(0);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    // Start polling on mount if no comments yet (for newly created discussions)
    useEffect(() => {
        if (comments.length === 0 && discussionId) {
            // New discussion with no comments - bot response is probably being generated
            startPollingForBotResponse(0);
        }
    }, [discussionId]); // Only run once on mount

    // Start polling when admin triggers bot from parent (DiscussionDetail)
    useEffect(() => {
        if (botTriggerCount > 0) {
            startPollingForBotResponse(comments.length);
        }
    }, [botTriggerCount]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll for bot response after posting
    const startPollingForBotResponse = useCallback((currentCommentCount: number) => {
        // Clear any existing polling
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
        }
        pollCountRef.current = 0;

        // Poll every 2 seconds, max 8 times (16 seconds total)
        pollIntervalRef.current = setInterval(async () => {
            pollCountRef.current += 1;

            try {
                const response = await discussionService.getDiscussion(discussionId);
                if (response.success && response.discussion.comments) {
                    const newComments = response.discussion.comments;
                    // Check if new comments have arrived (bot response)
                    if (newComments.length > currentCommentCount) {
                        refreshComments(discussionId, newComments);
                        // Stop polling once we get the bot response
                        if (pollIntervalRef.current) {
                            clearInterval(pollIntervalRef.current);
                            pollIntervalRef.current = null;
                        }
                    }
                }
            } catch (error) {
                console.error('Error polling for bot response:', error);
            }

            // Stop polling after max attempts
            if (pollCountRef.current >= 8) {
                if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
            }
        }, 2000);
    }, [discussionId, refreshComments]);

    // Confirmation modal state
    const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
        isOpen: boolean;
        commentId: string | null;
    }>({ isOpen: false, commentId: null });

    // Edit state
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editContent, setEditContent] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    // Bot trigger state
    const [triggeringBotCommentId, setTriggeringBotCommentId] = useState<string | null>(null);
    const [botPromptModal, setBotPromptModal] = useState<{
        isOpen: boolean;
        commentId: string | null;
    }>({ isOpen: false, commentId: null });

    const startEditingComment = (comment: DiscussionComment) => {
        setEditingCommentId(comment.comment_id);
        setEditContent(comment.content);
    };

    const cancelEditingComment = () => {
        setEditingCommentId(null);
        setEditContent('');
    };

    const saveEditComment = async (commentId: string) => {
        if (!editContent.trim()) {
            toast.error('Comment cannot be empty');
            return;
        }

        if (editContent.trim().length < MIN_COMMENT_LENGTH) {
            toast.error(`Comment must be at least ${MIN_COMMENT_LENGTH} characters`);
            return;
        }

        setSavingEdit(true);
        try {
            await discussionService.editComment(discussionId, commentId, editContent.trim());
            toast.success('Comment updated');
            setEditingCommentId(null);
            // Update store directly instead of refetching
            updateCommentContent(discussionId, commentId, editContent.trim());
            setEditContent('');
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to update comment');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleTriggerBotForComment = async (commentId: string, adminInstruction?: string) => {
        setTriggeringBotCommentId(commentId);
        try {
            await discussionService.triggerBotResponseForComment(discussionId, commentId, adminInstruction);
            toast.success('Bot response triggered! It will appear shortly.');
            setBotPromptModal({ isOpen: false, commentId: null });
            // Start polling for bot response
            startPollingForBotResponse(comments.length);
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to trigger bot response');
        } finally {
            setTriggeringBotCommentId(null);
        }
    };

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
            toast.error('Only JPG and PNG images are allowed');
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size must be less than 5MB');
            return;
        }

        setCommentImage(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setCommentImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmitComment = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!newComment.trim() || !user) {
            toast.error('Comment cannot be empty');
            return;
        }

        if (newComment.trim().length < MIN_COMMENT_LENGTH) {
            toast.error(`Comment must be at least ${MIN_COMMENT_LENGTH} characters`);
            return;
        }

        setSubmitting(true);
        try {
            const response = await discussionService.addComment(discussionId, {
                content: newComment.trim(),
                image: commentImage || undefined,
            });

            // Create new comment object for optimistic update
            const newCommentObj: DiscussionComment = {
                comment_id: response.comment_id,
                user_id: user._id,
                user_name: user.full_name || user.username,
                username: user.username,
                user_profile_picture: user.profile_picture || user.picture,
                content: newComment.trim(),
                upvote_count: 0,
                downvote_count: 0,
                upvotes: [],
                downvotes: [],
                created_at: new Date().toISOString(),
            };

            // Add to store directly instead of refetching
            addCommentToStore(discussionId, newCommentObj);

            setNewComment('');
            setCommentImage(null);
            setImagePreview(null);
            toast.success('Comment added');

            // Start polling for bot response (current count + 1 for the comment we just added)
            startPollingForBotResponse(comments.length + 1);
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to add comment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleVoteComment = async (commentId: string, voteType: 'up' | 'down') => {
        if (!isAuthenticated || !user?._id) {
            toast.error('Please login to vote');
            return;
        }

        try {
            // Call API and get updated counts
            const response = voteType === 'up'
                ? await discussionService.upvoteComment(discussionId, commentId)
                : await discussionService.downvoteComment(discussionId, commentId);

            // Update store directly instead of refetching
            updateCommentVote(
                discussionId,
                commentId,
                user._id,
                voteType,
                response.upvote_count,
                response.downvote_count
            );
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to vote');
        }
    };

    const handleDeleteComment = (commentId: string) => {
        setDeleteConfirmModal({ isOpen: true, commentId });
    };

    const confirmDeleteComment = async () => {
        if (!deleteConfirmModal.commentId) return;

        try {
            await discussionService.deleteComment(discussionId, deleteConfirmModal.commentId);
            toast.success('Comment deleted');
            // Remove from store directly instead of refetching
            removeComment(discussionId, deleteConfirmModal.commentId);
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to delete comment');
        } finally {
            setDeleteConfirmModal({ isOpen: false, commentId: null });
        }
    };

    const formatDate = (date: string) => {
        const relative = formatRelativeTime(date);
        if (relative.endsWith('ago') || relative === 'just now') return relative;
        return formatDisplayDate(date);
    };

    const isAdmin = user?.is_admin;

    return (
        <div className="space-y-6">
            {/* Discussions Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white font-serif">
                    Discussions ({comments.length})
                </h2>
            </div>

            {/* Add Comment Form */}
            {isAuthenticated ? (
                <CommentInput
                    user={user}
                    newComment={newComment}
                    setNewComment={setNewComment}
                    commentImage={commentImage}
                    imagePreview={imagePreview}
                    submitting={submitting}
                    handleSubmitComment={handleSubmitComment}
                    handleImageSelect={handleImageSelect}
                    handleRemoveImage={handleRemoveImage}
                    fileInputRef={fileInputRef}
                    participants={participants}
                />
            ) : (
                <div className="bg-white/[0.08] border border-white/20 rounded-3xl p-6 text-center">
                    <p className="text-purple-300/60 font-sans">
                        Please login to comment
                    </p>
                </div>
            )}

            {/* Comments List */}
            <div className="space-y-4">
                {comments.length === 0 ? (
                    <div className="text-center py-12 bg-white/[0.08] border border-white/20 rounded-3xl">
                        <p className="text-purple-300/60 font-sans">
                            No comments yet. Be the first to comment!
                        </p>
                    </div>
                ) : (
                    comments.map((comment) => {
                        const hasUpvoted = comment.upvotes?.includes(user?._id || '');
                        const hasDownvoted = comment.downvotes?.includes(user?._id || '');
                        const isOwner = user?._id === comment.user_id;

                        return (
                            <div
                                key={comment.comment_id}
                                className="bg-white/[0.08] border border-white/20 rounded-3xl p-6 relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500/5 blur-[80px] rounded-full -translate-y-1/2 translate-x-1/2" />

                                <div className="relative z-10">
                                    {/* Comment Header */}
                                    <div className="flex items-start justify-between gap-4 mb-4">
                                        <div className="flex items-center gap-3 flex-1">
                                            {/* User Avatar */}
                                            {comment.user_profile_picture && comment.user_profile_picture.trim() !== '' ? (
                                                <img
                                                    src={comment.user_profile_picture}
                                                    alt={comment.user_name}
                                                    className="w-10 h-10 rounded-full border-2 border-white/20 object-cover"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = 'none';
                                                        const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                                        if (fallback) fallback.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm"
                                                style={{ display: comment.user_profile_picture && comment.user_profile_picture.trim() !== '' ? 'none' : 'flex' }}
                                            >
                                                {comment.user_name?.[0]?.toUpperCase() || 'U'}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <span className="text-white font-bold text-sm font-sans block">
                                                    {comment.user_name}
                                                </span>
                                                {comment.username && (
                                                    <span className="text-purple-300/50 text-xs font-sans block">
                                                        @{comment.username}
                                                    </span>
                                                )}
                                                <span className="text-purple-300/60 text-xs font-sans">
                                                    {formatDate(comment.created_at)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Edit, Delete & Bot Buttons */}
                                        {(isOwner || isAdmin) && (
                                            <div className="flex items-center gap-1">
                                                {isAdmin && (
                                                    <button
                                                        onClick={() => setBotPromptModal({ isOpen: true, commentId: comment.comment_id })}
                                                        disabled={triggeringBotCommentId === comment.comment_id}
                                                        className="p-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all disabled:opacity-50"
                                                        title="Trigger AI Reply"
                                                    >
                                                        <Bot size={16} />
                                                    </button>
                                                )}
                                                {editingCommentId !== comment.comment_id && (
                                                    <button
                                                        onClick={() => startEditingComment(comment)}
                                                        className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 transition-all"
                                                        title="Edit comment"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteComment(comment.comment_id)}
                                                    className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-all"
                                                    title="Delete comment"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Comment Content */}
                                    {editingCommentId === comment.comment_id ? (
                                        <div className="mb-4 bg-white/[0.04] border border-white/10 rounded-2xl p-4 space-y-3">
                                            <textarea
                                                value={editContent}
                                                onChange={(e) => setEditContent(e.target.value)}
                                                placeholder="Join the discussion..."
                                                rows={3}
                                                autoFocus
                                                maxLength={MAX_CONTENT_LENGTH}
                                                className="w-full bg-transparent border-none text-white text-base placeholder:text-white/30 focus:outline-none font-sans resize-none"
                                                disabled={savingEdit}
                                            />
                                            {/* Character Counter - Twitter style */}
                                            <div className="flex items-center justify-end gap-2">
                                                {editContent.length < MIN_COMMENT_LENGTH && (
                                                    <span className="text-xs text-yellow-400/80 font-sans">
                                                        Min {MIN_COMMENT_LENGTH} chars
                                                    </span>
                                                )}
                                                <span className={`text-xs font-sans ${
                                                    editContent.length < MIN_COMMENT_LENGTH
                                                        ? 'text-yellow-400'
                                                        : editContent.length > MAX_CONTENT_LENGTH - 100
                                                            ? 'text-red-400'
                                                            : editContent.length > MAX_CONTENT_LENGTH - 500
                                                                ? 'text-yellow-400'
                                                                : 'text-purple-300/60'
                                                }`}>
                                                    {editContent.length}/{MAX_CONTENT_LENGTH}
                                                </span>
                                            </div>

                                            {/* Divider */}
                                            <div className="border-t border-white/10" />

                                            {/* Actions */}
                                            <div className="flex items-center justify-end gap-3">
                                                <button
                                                    onClick={cancelEditingComment}
                                                    disabled={savingEdit}
                                                    className="px-4 py-2 text-white/60 hover:text-white font-bold rounded-full transition-all text-sm font-sans disabled:opacity-50"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => saveEditComment(comment.comment_id)}
                                                    disabled={savingEdit || !editContent.trim() || editContent.trim().length < MIN_COMMENT_LENGTH}
                                                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold py-2 px-6 rounded-full transition-all shadow-lg text-sm font-sans disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                                                >
                                                    {savingEdit ? 'Saving...' : 'Save'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mb-4 text-sm md:text-base font-sans">
                                            <MarkdownContent content={comment.content} />
                                        </div>
                                    )}

                                    {/* Comment Image */}
                                    {comment.image_filename && comment.user_id && (
                                        <div className="mb-4 flex justify-center">
                                            <DiscussionImage
                                                filename={comment.image_filename}
                                                userId={comment.user_id}
                                                alt="Comment image"
                                                className="max-h-64 w-auto rounded-2xl border border-white/20 block"
                                            />
                                        </div>
                                    )}

                                    {/* Voting */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleVoteComment(comment.comment_id, 'up')}
                                            disabled={!isAuthenticated}
                                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all font-sans text-sm ${hasUpvoted
                                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                : 'bg-white/5 hover:bg-white/10 text-white/60 hover:text-white border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed'
                                                }`}
                                        >
                                            <ArrowUp size={14} />
                                            <span>{comment.upvote_count}</span>
                                        </button>

                                        <button
                                            onClick={() => handleVoteComment(comment.comment_id, 'down')}
                                            disabled={!isAuthenticated}
                                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all font-sans text-sm ${hasDownvoted
                                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                                : 'bg-white/5 hover:bg-white/10 text-white/60 hover:text-white border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed'
                                                }`}
                                        >
                                            <ArrowDown size={14} />
                                            <span>{comment.downvote_count}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Delete Comment Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteConfirmModal.isOpen}
                onClose={() => setDeleteConfirmModal({ isOpen: false, commentId: null })}
                onConfirm={confirmDeleteComment}
                title="Delete Comment"
                message="Are you sure you want to delete this comment? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />

            {isAdmin && (
                <AdminInstructionModal
                    isOpen={botPromptModal.isOpen}
                    onClose={() => setBotPromptModal({ isOpen: false, commentId: null })}
                    onSubmit={async (instruction) => {
                        if (!botPromptModal.commentId) return;
                        await handleTriggerBotForComment(botPromptModal.commentId, instruction);
                    }}
                    loading={Boolean(triggeringBotCommentId)}
                    title="Edit Prompt"
                    placeholder="Describe how you'd like AI to reply (optional). Example: User asked pricing. Show all plans and suggest Quick for budget users."
                />
            )}
        </div>
    );
};

export default CommentSection;
